/**
 * A seed for an independent stream of the same session (FNV-1a over the label). Systems that draw
 * from their own stream cannot shift the traffic, readbacks or compliance delays of a seeded run,
 * so adding one leaves existing scenarios and tests exactly as they were.
 */
export function deriveSeed(seed: number, label: string): number {
  let h = (2166136261 ^ (seed >>> 0)) >>> 0;
  for (let i = 0; i < label.length; i++) {
    h = (h ^ label.charCodeAt(i)) >>> 0;
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h || 1;
}

/** Seeded PRNG (mulberry32) so scenarios and tests are reproducible. */
export class Rng {
  private s: number;
  constructor(seed = 1) { this.s = seed >>> 0 || 1; }
  snapshot(): number { return this.s; }
  restore(state: number): void { this.s = state >>> 0; }
  next(): number {
    this.s = (this.s + 0x6d2b79f5) >>> 0;
    let t = this.s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(min: number, max: number): number { return min + (max - min) * this.next(); }
  int(min: number, max: number): number { return Math.floor(this.range(min, max + 1)); }
  pick<T>(a: readonly T[]): T { return a[Math.floor(this.next() * a.length)]!; }
  weighted<T>(items: readonly (readonly [T, number])[]): T {
    const total = items.reduce((s, [, w]) => s + w, 0);
    let r = this.next() * total;
    for (const [v, w] of items) { r -= w; if (r <= 0) return v; }
    return items[items.length - 1]![0];
  }
  /** Exponential inter-arrival time for a Poisson process with `perHour` events. */
  exponentialS(perHour: number): number { return perHour <= 0 ? Infinity : (-Math.log(1 - this.next()) * 3600) / perHour; }
}
